<template>
    <div>
        <v-style v-if="caseSettings.link_color">
            .rich-text-content a { color: {{ caseSettings.link_color }}; }
        </v-style>
        <v-style v-if="caseSettings.heading_1_color">
            .rich-text-content h1 { color: {{ caseSettings.heading_1_color }}; }
        </v-style>
        <v-style v-if="caseSettings.heading_2_color">
            .rich-text-content h2 { color: {{ caseSettings.heading_2_color }}; }
        </v-style>
        <v-style v-if="caseSettings.heading_3_color">
            .rich-text-content h3 { color: {{ caseSettings.heading_3_color }}; }
        </v-style>
        <v-style>
            .rich-text-content p, .rich-text-content li, .global-theme-text { 
                font-size: {{ caseSettingsValidated.body_font_size }}px !important;
            }
            .rich-text-content h1 { font-size: {{ caseSettingsValidated.heading_1_font_size }}px !important; }
            .rich-text-content h2 { font-size: {{ caseSettingsValidated.heading_2_font_size }}px !important; }
            .rich-text-content h3 { font-size: {{ caseSettingsValidated.heading_3_font_size }}px !important; }
            .rich-text-content .custom-p-sub { font-size: {{ caseSettingsValidated.subtext_font_size }}px !important; }

            .panel-header {
                font-size: {{ caseSettingsValidated.body_font_size * 1.2 }}px !important;
            }
            .node-container .modal-title {
                font-size: {{ caseSettingsValidated.body_font_size * 1.2 }}px !important;
            }
            .btn-session {
                font-size: {{ btnFontSize }}px !important;
            }

            .global-theme .custom-control {
                padding-left: {{ 1.5 * caseSettingsValidated.body_font_size }}px !important;
            }
            .global-theme .custom-control-label::before, .global-theme .custom-control-label::after {
                left: -{{ Math.floor(1.5 * caseSettingsValidated.body_font_size) }}px !important;
                width: {{ caseSettingsValidated.body_font_size }}px !important;
                height: {{ caseSettingsValidated.body_font_size }}px !important;
            }
        </v-style>
        <div v-if="caseSettings.global_font_family_name">
            <link :href="globalFontUrl" rel="stylesheet">
            <v-style>
                .global-theme:not(.no-theme), .rich-text-content {
                    font-family: {{ globalFontFamilyString }};
                }
            </v-style>
            <v-style v-if="caseSessionActive">
                <!-- Popovers -->
                .hotspot-popover {
                    font-family: {{ globalFontFamilyString }};
                }
            </v-style>
        </div>
        <v-style v-if="caseSessionActive && caseSettings.global_theme_color">
            <!-- Screen Background -->
            #app, html {
                background-color: {{ themeColors.background }} !important;
            }
            <!-- Loading Spinners -->
            .spinner-grow.text-brand {
                color: {{ themeColors.secondary }} !important;
            }
        </v-style>
        <v-style v-else-if="caseSessionActive">
            <!-- Default Screen Background -->
            #app, html {
                background-color: {{ themeColors.background }} !important;
            }
        </v-style>
        <v-style v-if="caseSettings.global_theme_color">
            <!-- Form Inputs -->
            .global-theme .custom-control-input:not(:disabled):active ~ .custom-control-label::before {
                background-color: {{ themeColors.formInputPressed }};
                border-color: {{ themeColors.formInputPressed }};
            }
            .global-theme .custom-control-input:checked ~ .custom-control-label::before {
                border-color: {{ themeColors.primary }};
                background-color: {{ themeColors.primary }};
            }
            .global-theme .form-control:focus:not(.is-invalid) {
                border-color: {{ themeColors.formInputBorderFocus }};
                -webkit-box-shadow: 0 0 0 0.05rem {{ themeColors.primary }};
                box-shadow: 0 0 0 0.05rem {{ themeColors.primary }};
            }
            .global-theme .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
                border-color: {{ themeColors.secondary }};
            }
            .global-theme .custom-control-input:disabled:checked ~ .custom-control-label::before {
                background-color: {{ setAlpha(themeColors.primary, 0.5) }} !important;
            }
            <!-- Brand Buttons -->
            .global-theme button.btn-brand {
                border-color: {{ themeColors.secondary }};
                background-color: {{ themeColors.secondary }};
            }
            .global-theme button.btn-brand:not(:disabled):hover {
                background-color: {{ darkenHex(themeColors.secondary, 5) }};
                border-color: {{ darkenHex(themeColors.secondary, 5) }};
            }
            .global-theme button.btn-brand:not(:disabled):not(.disabled):active {
                background-color: {{ darkenHex(themeColors.secondary, 10) }};
                border-color: {{ darkenHex(themeColors.secondary, 10) }};
            }
            <!-- Branch Buttons -->
            .global-theme button.btn-branch {
                border-color: {{ themeColors.secondary }};
                background-color: {{ themeColors.secondary }};
            }
            .global-theme button.btn-branch:not(:disabled):hover {
                border-color: {{ darkenHex(themeColors.secondary, 5) }};
                background-color: {{ darkenHex(themeColors.secondary, 5) }};
            }
            .global-theme button.btn-branch:not(:disabled):not(.disabled):active {
                background-color: {{ darkenHex(themeColors.secondary, 10) }};
                border-color: {{ darkenHex(themeColors.secondary, 10) }};
            }
            <!-- All Buttons -->
            .global-theme .btn:focus {
                -webkit-box-shadow: 0 0 0 2px {{ themeColors.primary }};
                box-shadow: 0 0 0 2px {{ themeColors.primary }};
            }
            <!-- Primary Text -->
            .global-theme .text-primary {
                color: {{ themeColors.text }} !important;
            }
            <!-- Primary Backgrounds -->
            .global-theme .bg-primary, .global-theme .progress-bar {
                background-color: {{ themeColors.primary }} !important;
            }
            <!-- Links -->
            .global-theme a:not([href^='#']):not(.btn-gray):not(.dropdown-item), .global-theme button.btn-link, .rich-text-content a:not([href^='#']) {
                color: {{ themeColors.text }};
            }
            .global-theme a:not([href^='#']):not(.btn-gray):not(.dropdown-item):hover, .global-theme button.btn-link:hover {
                color: {{ darkenHex(themeColors.text, 12) }};
            }
            <!-- Unexplored Markers -->
            .unexplored-marker {
                background-color: {{ themeColors.primary }} !important;
            }
            .unexplored-btn-marker {
                background-color: {{ themeColors.unexploredMarker }} !important;
            }
            <!-- Video Player -->
            .global-theme .vjs-skin .vjs-volume-level,
            .global-theme .vjs-skin .vjs-play-progress,
            .global-theme .vjs-skin .vjs-slider-bar
            {
                background-color: {{ themeColors.primary }} !important;
            }
            <!-- Tabs -->
            .global-theme .tab-label {
                color: {{ darkenHex(themeColors.primary, 5) }}
            }
            .global-theme .nav-item a[href].nav-link  {
                color: {{ themeColors.text }};
            }
            .global-theme .nav-item:hover {
                color: {{ darkenHex(themeColors.primary, 5) }}
            }
            <!-- Categories -->
            .global-theme .dropzone-ready {
                border-color: {{ themeColors.primary }};
            }
        </v-style>
        <v-style v-else>
            <!-- Brand Buttons -->
            .global-theme button.btn-brand {
                border-color: {{ themeColors.secondary }};
                background-color: {{ themeColors.secondary }};
            }
            .global-theme button.btn-brand:not(:disabled):hover {
                background-color: {{ darkenHex(themeColors.secondary, 5) }};
                border-color: {{ darkenHex(themeColors.secondary, 5) }};
            }
            .global-theme button.btn-brand:not(:disabled):not(.disabled):active {
                background-color: {{ darkenHex(themeColors.secondary, 10) }};
                border-color: {{ darkenHex(themeColors.secondary, 10) }};
            }
        </v-style>
    </div>
</template>

<script>
import tinycolor from "tinycolor2"
import { globalThemeOptions } from "@/assets/js/settings.js"

export default {
    name: "GlobalStyleBlock",
    props: {
        caseSettings: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {}
    },
    computed: {
        caseSessionActive() {
            return this.$route.meta.caseSessionIsActive
        },
        caseSettingsValidated() {
            let baseFontSize = this.caseSettings.body_font_size
            let heading1Size = this.caseSettings.heading_1_font_size
            let heading2Size = this.caseSettings.heading_2_font_size
            let heading3Size = this.caseSettings.heading_3_font_size

            if (!baseFontSize || baseFontSize === 0) {
                baseFontSize = 16
            }
            if (!heading1Size || heading1Size === 0) {
                heading1Size = baseFontSize * 1.5
            }
            if (!heading2Size || heading2Size === 0) {
                heading2Size = baseFontSize * 1.25
            }
            if (!heading3Size || heading3Size === 0) {
                heading3Size = baseFontSize * 1
            }

            return {
                body_font_size: baseFontSize,
                heading_1_font_size: heading1Size,
                heading_2_font_size: heading2Size,
                heading_3_font_size: heading3Size,
                subtext_font_size: baseFontSize * 0.9
            }
        },
        btnFontSize () {
            if (this.caseSettingsValidated.body_font_size >= 19) {
                return 19
            } else if (this.caseSettingsValidated.body_font_size >= 18) {
                return 18
            }
            return this.caseSettingsValidated.body_font_size + 1
        },
        globalFontUrl() {
            return `https://fonts.googleapis.com/css2?family=${this.caseSettings.global_font_family_name}:wght@400;500;600;700&display=swap`
        },
        globalFontFamilyString() {
            return `"${ this.caseSettings.global_font_family_name }", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", 
            Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";`
        },
        baseThemeColors() {
            if (!this.caseSettings.global_theme_color) {
                // Select one of the themes even if not configured, just so the other
                // computed properties don't throw errors.
                return globalThemeOptions["tealnavy"]
            }
            return globalThemeOptions[this.caseSettings.global_theme_color]
        },
        themePrimary() {
            return this.baseThemeColors.primary
        },
        themeSecondary() {
            return this.baseThemeColors.secondary
        },
        themeBackgroundColor() {
            return this.baseThemeColors.background
        },
        themeColors() {
            return {
                primary: this.themePrimary,
                secondary: this.themeSecondary,
                background: this.themeBackgroundColor,
                formInputPressed: tinycolor(this.themePrimary).desaturate(50).lighten(50).toHexString(),
                formInputDisabled: tinycolor(this.themePrimary).desaturate(50).lighten(50).toHexString(),
                formInputBorderFocus: tinycolor(this.themePrimary).desaturate(30).lighten(12).toHexString(),
                text: tinycolor(this.themePrimary).saturate(20).darken(this.baseThemeColors.textDarkenPercent).toHexString(),
                unexploredMarker: tinycolor(this.themeSecondary).saturate(100).lighten(45).toHexString()
            }
        },
    },
    methods: {
        lightenHex(hex, amount) {
            return tinycolor(hex).lighten(amount).toHexString()
        },
        darkenHex(hex, amount) {
            return tinycolor(hex).darken(amount).toHexString()
        },
        setAlpha(hex, amount) {
            return tinycolor(hex).setAlpha(amount).toRgbString()
        }
    },
}
</script>

<style lang="scss">
@import "@/assets/styles/variables.scss";

.rich-text-content {
    h1, h2, h3, h4, p, li {
        color: #383c40;
    }

    h1, h2, h3, h4 {
        line-height: 1.5 !important;
        font-weight: 600;
    }

    .custom-p-sub {
        color: $secondary;
    }

    strong {
        font-weight: 700 !important;
    }

    p {
        margin-bottom: 0px;
        line-height: 1.58;
    }

    ol, ul {
        margin-bottom: 0px;
    }
}
</style>